/**
 * Waste / wastewater breakout against the live GHG calculation.
 *
 * fetchGHGInventoryData Waste & wastewater =
 *   landfill (tons × county factor) + scaled ClimatePlans wastewater.
 * This helper splits that combined total when a wastewater observation exists.
 * Never assign missing wastewater zero.
 */

import { WASTEWATER_SOURCE_CONCLUSION } from './wastewaterSource';

export const WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT = 0.51;

export type WasteBreakoutStatus =
  | 'reconciled'
  | 'unresolved_breakout'
  | 'combined_only'
  | 'displayed_waste_excludes_wastewater';

export interface WasteWastewaterReconciliation {
  status: WasteBreakoutStatus;
  combinedWasteMt: number | null;
  solidWasteMt: number | null;
  wastewaterMt: number | null;
  residualMt: number | null;
  toleranceMt: number;
  displayedWasteIncludesWastewater: boolean;
  sourceConclusion: typeof WASTEWATER_SOURCE_CONCLUSION.statementLabel;
  note: string;
}

export function reconcileWasteWastewater(input: {
  combinedWasteMt: number | null;
  solidWasteMt: number | null;
  wastewaterMt: number | null;
}): WasteWastewaterReconciliation {
  const { combinedWasteMt, solidWasteMt, wastewaterMt } = input;
  const displayedWasteIncludesWastewater = WASTEWATER_SOURCE_CONCLUSION.liveWasteIncludesWastewater;
  if (wastewaterMt == null) {
    return {
      status: 'unresolved_breakout',
      combinedWasteMt,
      solidWasteMt: solidWasteMt ?? combinedWasteMt,
      wastewaterMt: null,
      residualMt: null,
      toleranceMt: WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT,
      displayedWasteIncludesWastewater,
      sourceConclusion: WASTEWATER_SOURCE_CONCLUSION.statementLabel,
      note:
        'The GHG inventory page combines landfill waste and wastewater as Waste & wastewater when a wastewater estimate exists. A year-matched or scaled wastewater observation was not returned for this inventory year and is not assigned zero.',
    };
  }
  if (solidWasteMt == null || combinedWasteMt == null) {
    return {
      status: 'unresolved_breakout',
      combinedWasteMt,
      solidWasteMt,
      wastewaterMt,
      residualMt: null,
      toleranceMt: WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT,
      displayedWasteIncludesWastewater,
      sourceConclusion: WASTEWATER_SOURCE_CONCLUSION.statementLabel,
      note: 'Solid-waste or displayed Waste totals are missing; wastewater is not converted to zero.',
    };
  }
  const residual = combinedWasteMt - (solidWasteMt + wastewaterMt);
  const ok = Math.abs(residual) <= WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT;
  if (!displayedWasteIncludesWastewater) {
    return {
      status: ok ? 'reconciled' : 'displayed_waste_excludes_wastewater',
      combinedWasteMt,
      solidWasteMt,
      wastewaterMt,
      residualMt: residual,
      toleranceMt: WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT,
      displayedWasteIncludesWastewater,
      sourceConclusion: WASTEWATER_SOURCE_CONCLUSION.statementLabel,
      note: ok
        ? 'Solid waste plus wastewater equals the displayed Waste total within the documented tolerance.'
        : 'Solid waste and wastewater are shown separately. The GHG inventory page combines them as Waste & wastewater; this report also keeps a wastewater breakout.',
    };
  }
  return {
    status: ok ? 'reconciled' : 'unresolved_breakout',
    combinedWasteMt,
    solidWasteMt,
    wastewaterMt,
    residualMt: residual,
    toleranceMt: WASTE_WASTEWATER_RECONCILE_TOLERANCE_MT,
    displayedWasteIncludesWastewater,
    sourceConclusion: WASTEWATER_SOURCE_CONCLUSION.statementLabel,
    note: ok
      ? 'Solid waste plus wastewater equals the software Waste total within the documented tolerance.'
      : 'Solid waste plus wastewater does not equal the software Waste total. Completeness is not claimed.',
  };
}
